/**
 * How a prepared checkout is finished, as a browser-safe value.
 *
 * This duplicates `CheckoutIntentKind` from the generated payments client on purpose, and the reason
 * is bundling rather than taste. That enum is a RUNTIME value, so importing it pulls the whole
 * `@wix/ai-scheduling-assistant-common-lib/rpc` barrel into the client bundle — and with it every
 * RPC client, Ambassador, `natural`, and a redis dependency. Webpack then cannot resolve `net`,
 * `tls`, `fs` or `child_process`, and the browser build fails outright.
 *
 * An `import type` erases at compile time and would be free; a value cannot be. So the numbers live
 * here, and the server-side API routes keep using the generated enum directly — they run in Node and
 * have no such constraint.
 *
 * **These are STRINGS, and that is the wire, not a stylistic choice.** The codegen runs with
 * `stringEnums`, so the generated client emits `'CHECKOUT_INTENT_PAYMENT'` and the API route
 * JSON-serializes it unchanged. Writing the proto's field NUMBERS here instead — which is what the
 * `.proto` shows and what I first did — makes every comparison false: the screen would never
 * recognise a payment or a setup, and would fall through to "nothing to confirm" on a purchase that
 * needed confirming. The test beside this pins each value against the generated enum for exactly
 * that reason.
 */
export const CheckoutIntentKind = {
  CHECKOUT_INTENT_KIND_UNKNOWN: 'CHECKOUT_INTENT_KIND_UNKNOWN',
  /** `stripe.confirmPayment` — there is money to take now. */
  CHECKOUT_INTENT_PAYMENT: 'CHECKOUT_INTENT_PAYMENT',
  /** `stripe.confirmSetup` — nothing due today, but a card must be stored for the renewal. */
  CHECKOUT_INTENT_SETUP: 'CHECKOUT_INTENT_SETUP',
  /** Nothing to confirm; the purchase is already settled. */
  CHECKOUT_INTENT_NONE: 'CHECKOUT_INTENT_NONE',
} as const;

export type CheckoutIntentKind = (typeof CheckoutIntentKind)[keyof typeof CheckoutIntentKind];
